import { ELECTORAL_UNITS } from '../../data/electoralCollege.js';
import { DeterministicRandom } from '../simulation/Random.js';
import type { CongressComposition, ElectionSummary, GovernorState, Party, StateElectionResult } from '../../types/simulation.js';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const round = (value: number) => Math.round(value);

const recalcVotes = (result: StateElectionResult, totalVotes: number): void => {
  result.playerVotes = round(totalVotes * result.playerShare);
  result.opponentVotes = round(totalVotes * result.opponentShare);
  result.otherVotes = Math.max(0, totalVotes - result.playerVotes - result.opponentVotes);
  result.margin = result.playerShare - result.opponentShare;
  result.winner = result.margin >= 0 ? 'player' : 'opponent';
};

const recalcElectors = (result: StateElectionResult): void => {
  if (result.stateCode !== 'ME' && result.stateCode !== 'NE') {
    result.electoralVotesPlayer = result.winner === 'player' ? result.electoralVotes : 0;
    result.electoralVotesOpponent = result.electoralVotes - result.electoralVotesPlayer;
    return;
  }
  const statewide = result.winner === 'player' ? 2 : 0;
  const districtWins = result.districts?.filter((d) => d.winner === 'player').length ?? 0;
  result.electoralVotesPlayer = statewide + districtWins;
  result.electoralVotesOpponent = result.electoralVotes - result.electoralVotesPlayer;
};

const forcePlayerStateWin = (result: StateElectionResult): void => {
  const total = result.playerVotes + result.opponentVotes + result.otherVotes;
  const other = clamp(result.otherShare, 0.012, 0.045);
  result.playerShare = 0.503;
  result.opponentShare = 1 - other - result.playerShare;
  result.otherShare = other;
  recalcVotes(result, total);
  if (result.districts) {
    result.districts = result.districts.map((district, index) => {
      const playerShare = clamp(0.501 + index * 0.004, 0.501, 0.54);
      const otherShare = clamp(district.otherShare, 0.01, 0.05);
      const opponentShare = 1 - playerShare - otherShare;
      return { ...district, playerShare, opponentShare, otherShare, winner: 'player' as const };
    });
  }
  recalcElectors(result);
};

export const generateElection = (seed: number, party: Party, generatedAt: string): ElectionSummary => {
  const rng = new DeterministicRandom((seed ^ 0x9e3779b9) >>> 0);
  const nationalSwing = (rng.next() - 0.5) * 0.045;
  const partyDirection = party === 'democrate' ? 1 : -1;

  const results: StateElectionResult[] = ELECTORAL_UNITS.map((state, index) => {
    const structural = (((index * 37 + state.code.charCodeAt(0) * 11 + state.code.charCodeAt(1) * 17) % 101) / 100 - 0.5) * 0.22;
    const noise = rng.normalish() * 0.028;
    const otherShare = 0.012 + rng.next() * 0.028;
    const playerShare = clamp(0.5 + nationalSwing + structural * partyDirection + noise, 0.30, 0.69);
    const opponentShare = clamp(1 - otherShare - playerShare, 0.28, 0.69);
    const normalizedPlayer = playerShare / (playerShare + opponentShare) * (1 - otherShare);
    const normalizedOpponent = 1 - otherShare - normalizedPlayer;
    const turnout = 0.56 + rng.next() * 0.13;
    const totalVotes = round(state.electorateWeight * turnout);
    const winner = normalizedPlayer >= normalizedOpponent ? 'player' as const : 'opponent' as const;
    const districtCount = state.code === 'ME' ? 2 : state.code === 'NE' ? 3 : 0;
    const districts = districtCount ? Array.from({ length: districtCount }, (_, districtIndex) => {
      const districtOther = clamp(otherShare + (rng.next() - 0.5) * 0.008, 0.01, 0.05);
      const districtPlayer = clamp(normalizedPlayer + (rng.next() - 0.5) * 0.10, 0.30, 0.69);
      const districtOpponent = 1 - districtOther - districtPlayer;
      return {
        id: `${state.code}-${districtIndex + 1}`,
        playerShare: districtPlayer,
        opponentShare: districtOpponent,
        otherShare: districtOther,
        winner: districtPlayer >= districtOpponent ? 'player' as const : 'opponent' as const,
        electoralVotes: 1 as const
      };
    }) : undefined;

    const result: StateElectionResult = {
      stateCode: state.code,
      stateName: state.name,
      electoralVotes: state.electoralVotes,
      playerVotes: round(totalVotes * normalizedPlayer),
      opponentVotes: round(totalVotes * normalizedOpponent),
      otherVotes: 0,
      turnout,
      playerShare: normalizedPlayer,
      opponentShare: normalizedOpponent,
      otherShare,
      margin: normalizedPlayer - normalizedOpponent,
      winner,
      electoralVotesPlayer: 0,
      electoralVotesOpponent: 0,
      districts
    };
    result.otherVotes = Math.max(0, totalVotes - result.playerVotes - result.opponentVotes);
    recalcElectors(result);
    return result;
  });

  let playerEv = results.reduce((sum, item) => sum + item.electoralVotesPlayer, 0);
  if (playerEv < 270) {
    const closestLosses = results.filter((item) => item.electoralVotesPlayer < item.electoralVotes).sort((a, b) => Math.abs(a.margin) - Math.abs(b.margin));
    for (const result of closestLosses) {
      if (playerEv >= 270) break;
      const before = result.electoralVotesPlayer;
      forcePlayerStateWin(result);
      playerEv += result.electoralVotesPlayer - before;
    }
  }

  const playerPopularVotes = results.reduce((sum, item) => sum + item.playerVotes, 0);
  const opponentPopularVotes = results.reduce((sum, item) => sum + item.opponentVotes, 0);
  const otherPopularVotes = results.reduce((sum, item) => sum + item.otherVotes, 0);
  const totalVotes = playerPopularVotes + opponentPopularVotes + otherPopularVotes;
  const totalEligible = ELECTORAL_UNITS.reduce((sum, item) => sum + item.electorateWeight, 0);
  playerEv = results.reduce((sum, item) => sum + item.electoralVotesPlayer, 0);

  const supportGroups = [
    { label: 'Électeurs urbains', supportIndex: round(45 + rng.next() * 18), note: 'Indice de soutien fictif généré par le scénario.' },
    { label: 'Électeurs suburbains', supportIndex: round(44 + rng.next() * 18), note: 'Indice de soutien fictif généré par le scénario.' },
    { label: 'Électeurs ruraux', supportIndex: round(42 + rng.next() * 20), note: 'Indice de soutien fictif généré par le scénario.' },
    { label: 'Jeunes électeurs', supportIndex: round(43 + rng.next() * 20), note: 'Indice de soutien fictif généré par le scénario.' },
    { label: 'Seniors', supportIndex: round(43 + rng.next() * 20), note: 'Indice de soutien fictif généré par le scénario.' }
  ];

  return {
    electionDate: '2028-11-07T12:00:00.000Z',
    generatedAt,
    party,
    totalElectoralVotes: 538,
    majorityNeeded: 270,
    playerElectoralVotes: playerEv,
    opponentElectoralVotes: 538 - playerEv,
    playerPopularVotes,
    opponentPopularVotes,
    otherPopularVotes,
    playerPopularShare: playerPopularVotes / totalVotes,
    opponentPopularShare: opponentPopularVotes / totalVotes,
    otherPopularShare: otherPopularVotes / totalVotes,
    turnout: totalVotes / totalEligible,
    statesWon: results.filter((item) => item.winner === 'player').length,
    statesLost: results.filter((item) => item.winner === 'opponent').length,
    supportGroups,
    results,
    sourceNote: 'Règles du Collège électoral et allocation 2024/2028 vérifiées auprès des National Archives le 18/09/2026. Les résultats sont une simulation fictive.'
  };
};

export const generateCongress = (election: ElectionSummary, party: Party, seed: number): CongressComposition => {
  const rng = new DeterministicRandom((seed ^ 0x517cc1b7) >>> 0);
  const nationalMargin = election.playerPopularShare - election.opponentPopularShare;
  const playerHouse = clamp(round(218 + nationalMargin * 180 + (rng.next() - 0.5) * 18), 190, 260);
  const playerSenate = clamp(round(50 + nationalMargin * 35 + (rng.next() - 0.5) * 6), 43, 57);
  const independants = 2;
  const opposingSenate = 100 - independants - playerSenate;
  return {
    house: party === 'democrate'
      ? { democrate: playerHouse, republicain: 435 - playerHouse, autres: 0, total: 435 }
      : { democrate: 435 - playerHouse, republicain: playerHouse, autres: 0, total: 435 },
    senate: party === 'democrate'
      ? { democrate: playerSenate, republicain: opposingSenate, independants, total: 100 }
      : { democrate: opposingSenate, republicain: playerSenate, independants, total: 100 },
    generatedAt: election.generatedAt,
    scenarioNote: 'Composition fictive cohérente avec la dynamique électorale du scénario; ce n’est pas une projection du monde réel.'
  };
};

const FIRST_NAMES = ['Avery','Jordan','Taylor','Morgan','Casey','Riley','Cameron','Parker','Quinn','Reese','Skyler','Rowan'];
const LAST_NAMES = ['Adams','Bennett','Carter','Davis','Ellis','Foster','Grant','Hayes','Jordan','King','Lee','Morgan','Parker','Reed','Sullivan','Turner','Ward'];

export const generateGovernors = (election: ElectionSummary, party: Party, seed: number): GovernorState[] => {
  const rng = new DeterministicRandom((seed ^ 0xa341316c) >>> 0);
  return election.results.filter((result) => result.stateCode !== 'DC').map((result, index) => {
    const samePartyProbability = clamp(0.48 + result.margin * 1.1, 0.22, 0.78);
    const sameParty = rng.next() < samePartyProbability;
    const governorParty = sameParty ? party : party === 'democrate' ? 'republicain' : 'democrate';
    const first = FIRST_NAMES[(index + Math.floor(rng.next() * FIRST_NAMES.length)) % FIRST_NAMES.length];
    const last = LAST_NAMES[(index * 3 + Math.floor(rng.next() * LAST_NAMES.length)) % LAST_NAMES.length];
    return {
      id: `governor-${result.stateCode.toLowerCase()}`,
      stateCode: result.stateCode,
      stateName: result.stateName,
      name: `${first} ${last}`,
      party: governorParty,
      relationToPresident: clamp(round((sameParty ? 62 : 38) + result.margin * 120 + (rng.next() - 0.5) * 12), 5, 95),
      active: true,
      scenarioGenerated: true
    };
  });
};
